export type CommissionSearchRow = {
  repCode?: unknown;
  clientName?: unknown;
  businessName?: unknown;
  notes?: unknown;
  verticalName?: unknown;
  repName?: unknown;
  status?: unknown;
  amount?: unknown;
  createdAt?: unknown;
  paidAt?: unknown;
  isManualCommission?: unknown;
  isTest?: unknown;
  createdBy?: unknown;
};

export type CommissionStatusFilter = "" | "pending" | "approved" | "paid";

export type CommissionFilters = {
  query?: string;
  status?: CommissionStatusFilter;
  dateFrom?: string;
  dateTo?: string;
};

export type CommissionSummary = {
  totalCount: number;
  totalAmount: number;
  outstandingAmount: number;
  pending: { count: number; amount: number };
  approved: { count: number; amount: number };
  paid: { count: number; amount: number };
};

function searchableValue(value: unknown) {
  return typeof value === "string" || typeof value === "number"
    ? String(value).toLocaleLowerCase()
    : "";
}

function localDateKey(value: unknown) {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function amountInCents(value: unknown) {
  const amount = Number(value);
  return Number.isFinite(amount) ? Math.round(amount * 100) : 0;
}

function spreadsheetText(value: unknown) {
  const text = value == null ? "" : String(value);
  const protectedText = /^[=+\-@]/.test(text) ? `'${text}` : text;
  return `"${protectedText.replace(/"/g, '""')}"`;
}

function spreadsheetAmount(value: unknown) {
  const amount = Number(value);
  return Number.isFinite(amount) ? (Math.round(amount * 100) / 100).toFixed(2) : "0.00";
}

export function buildCommissionExportCsv(rows: readonly CommissionSearchRow[]) {
  const headers = [
    "Rep",
    "Client Name",
    "Business Name",
    "Description",
    "Vertical",
    "Date",
    "Rep Name",
    "Amount",
    "Status",
    "Paid Date",
    "Commission Type",
    "Test Record",
    "Created By",
  ];
  const dataRows = rows.map(row => [
    spreadsheetText(row.repCode),
    spreadsheetText(row.clientName),
    spreadsheetText(row.businessName),
    spreadsheetText(row.isManualCommission ? row.notes : ""),
    spreadsheetText(row.verticalName),
    spreadsheetText(localDateKey(row.createdAt)),
    spreadsheetText(row.repName),
    spreadsheetAmount(row.amount),
    spreadsheetText(searchableValue(row.status)),
    spreadsheetText(localDateKey(row.paidAt)),
    spreadsheetText(row.isManualCommission ? "Manual" : "Automatic"),
    spreadsheetText(row.isTest ? "Yes" : "No"),
    spreadsheetText(row.isManualCommission ? row.createdBy : ""),
  ]);

  return `\uFEFF${[
    headers.map(spreadsheetText),
    ...dataRows,
  ].map(row => row.join(",")).join("\r\n")}`;
}

export function filterCommissionRows<T extends CommissionSearchRow>(
  rows: readonly T[],
  filters: string | CommissionFilters
) {
  const options = typeof filters === "string" ? { query: filters } : filters;
  const normalizedQuery = (options.query ?? "").trim().toLocaleLowerCase();
  const status = options.status ?? "";
  const dateFrom = options.dateFrom ?? "";
  const dateTo = options.dateTo ?? "";

  if (dateFrom && dateTo && dateFrom > dateTo) return [];

  return rows.filter(row => {
    if (status && searchableValue(row.status) !== status) return false;

    if (dateFrom || dateTo) {
      const rowDate = localDateKey(row.createdAt);
      if (!rowDate) return false;
      if (dateFrom && rowDate < dateFrom) return false;
      if (dateTo && rowDate > dateTo) return false;
    }

    if (!normalizedQuery) return true;
    return [
      row.repCode,
      row.clientName,
      row.businessName,
      row.notes,
      row.verticalName,
      row.repName,
      row.status,
    ].some(value => searchableValue(value).includes(normalizedQuery));
  });
}

export function summarizeCommissionRows(
  rows: readonly CommissionSearchRow[]
): CommissionSummary {
  const cents = {
    total: 0,
    pending: 0,
    approved: 0,
    paid: 0,
  };
  const counts = { pending: 0, approved: 0, paid: 0 };

  for (const row of rows) {
    const amount = amountInCents(row.amount);
    cents.total += amount;
    const status = searchableValue(row.status);
    if (status === "pending" || status === "approved" || status === "paid") {
      counts[status] += 1;
      cents[status] += amount;
    }
  }

  return {
    totalCount: rows.length,
    totalAmount: cents.total / 100,
    outstandingAmount: (cents.pending + cents.approved) / 100,
    pending: { count: counts.pending, amount: cents.pending / 100 },
    approved: { count: counts.approved, amount: cents.approved / 100 },
    paid: { count: counts.paid, amount: cents.paid / 100 },
  };
}

export function currentMonthPayoutRange(
  half: "first" | "second",
  today = new Date()
) {
  const year = today.getFullYear();
  const monthIndex = today.getMonth();
  const toKey = (date: Date) => {
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${date.getFullYear()}-${month}-${day}`;
  };
  return half === "first"
    ? {
        dateFrom: toKey(new Date(year, monthIndex, 1)),
        dateTo: toKey(new Date(year, monthIndex, 15)),
      }
    : {
        dateFrom: toKey(new Date(year, monthIndex, 16)),
        dateTo: toKey(new Date(year, monthIndex + 1, 0)),
      };
}
